// Stage 200: the Laughter subscriber hook.
//
// Watches the host's useGadflyTurn snapshot. On the turnEnded ↑
// false→true transition (one fire per turnId), reads the settled
// bites + swats, filters to ACCEPTed bites, builds one Shape-Q
// engram per such bite, and POSTs each.
//
// One-shot per turnId — the per-turnId fired marker is held in a ref so
// React-Strict-Mode's double-invocation does not double-post. After
// the turnId changes (a new gadfly_bites event), the prior turnId's
// fired marker is dropped (we only need "did THIS turnId fire?",
// the prior turnId can never re-fire because the snapshot no longer
// references it).

import { useEffect, useRef } from 'react';
import { useGadflyTurn } from '../../hooks/useGadflyTurn';
import { useParallaxSlot } from '../../hooks/useParallaxSlot';
import { laughterDeposit } from '../../api/laughter';
import type { MoodInfo } from '../../hooks/useChat';
import { finalTagOf } from './finalTag';
import { buildEngram } from './buildEngram';
import { buildCorrelations } from './buildCorrelations';
import { applyLaughterFired } from './useLaughterPulse';

interface UseLaughterSubscriberArgs {
  enabled: boolean;
  conversationId: string | null;
  // The persona's response text + the user's message for the current turn.
  // Both come from useChat's messages array; AuthedApp pulls them by
  // walking the messages backward from the latest assistant message.
  userMessage: string | null;
  personaResponse: string | null;
  /** Stage 201-X: whether to publish to useLaughterPulse on fire. When
   *  false, the WARP ride-along marker never receives a pulse (the
   *  recognition still happens; we just don't announce it). */
  warpMarkerEnabled: boolean;
  /** Stage 201-P: when true, pack the Parallax cup-read + mood snapshot
   *  into the engram at fire time (correlation, not behaviour). */
  correlationsEnabled: boolean;
  /** Stage 201-P: the latest assistant message's MoodInfo, threaded from
   *  AuthedApp. null when no mood-bearing message exists this turn. */
  lastMood: MoodInfo | null;
}

export function useLaughterSubscriber(args: UseLaughterSubscriberArgs): void {
  const {
    enabled,
    conversationId,
    userMessage,
    personaResponse,
    warpMarkerEnabled,
    correlationsEnabled,
    lastMood,
  } = args;
  const turn = useGadflyTurn();
  // Stage 201-P: poll the Parallax slot ONLY when correlations are on.
  // Passing null short-circuits the hook's polling interval, so operators
  // running Laughter-on / correlations-off pay no network cost.
  const parallax = useParallaxSlot(
    correlationsEnabled ? conversationId : null,
  );
  const firedForTurnIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (!conversationId) return;
    if (!turn.turnId) return;
    if (!turn.turnEnded) return;
    if (firedForTurnIdRef.current === turn.turnId) return;
    if (turn.failed) {
      // Failed Gadfly call → no bites, no laughs; mark as fired so we
      // don't loop on re-renders.
      firedForTurnIdRef.current = turn.turnId;
      return;
    }
    if (!turn.bites || turn.bites.length === 0) {
      firedForTurnIdRef.current = turn.turnId;
      return;
    }

    // Mark fired BEFORE the async work — prevents Strict-Mode double-fire.
    firedForTurnIdRef.current = turn.turnId;

    const acceptedSwats = turn.swatEntries.filter((e) => e.tag === 'ACCEPT');
    if (acceptedSwats.length === 0) return;

    const firedAt = new Date().toISOString();
    const turnIdSnapshot = turn.turnId;
    const bitesSnapshot = turn.bites;
    const swatsSnapshot = turn.swatEntries;

    // Stage 201-P: snapshot the cross-pillar state ONCE at fire time so
    // every engram from this turn shares the same correlation reading.
    // undefined (flag off) → omit the field; an object (even all-null)
    // → keep it (we asked, this is what we saw).
    const correlations = correlationsEnabled
      ? buildCorrelations({ slot: parallax.slot, mood: lastMood })
      : undefined;

    // Sequential to keep ordering stable in the substrate, but losses
    // are independent — a 5xx on one engram does not block siblings.
    void (async () => {
      for (const acceptedSwat of acceptedSwats) {
        const acceptedBite = bitesSnapshot.find(
          (b) => b.index === acceptedSwat.biteIndex,
        );
        if (!acceptedBite) continue;
        // Re-check final tag — a later DEFER on this bite would mean
        // the swat we picked up isn't actually the final one.
        if (finalTagOf(acceptedBite, swatsSnapshot) !== 'ACCEPT') continue;
        // Stage 201-X: announce the fire to the in-isolate pulse store so
        // WARP (if mounted) can mark the bite billboard. We fire on
        // RECOGNITION, not on deposit success — the witness-discovery
        // framing is that the laugh happened the moment the persona
        // finalised the ACCEPT, not the moment Cloudflare wrote the row.
        if (warpMarkerEnabled) {
          applyLaughterFired({
            turnId: turnIdSnapshot,
            biteIndex: acceptedBite.index,
          });
        }
        try {
          const engram = buildEngram({
            acceptedBite,
            acceptedSwat,
            allBites: bitesSnapshot,
            allSwats: swatsSnapshot,
            userMessage: userMessage ?? '',
            personaResponse: personaResponse ?? '',
            turnId: turnIdSnapshot,
            conversationId,
            firedAt,
            correlations,
          });
          await laughterDeposit(engram);
        } catch (err) {
          // Silent on failure (per TINS — fail-silent on deposit
          // errors). Log once per error for operator forensics.
          // eslint-disable-next-line no-console
          console.warn('[laughter] deposit failed:', err);
        }
      }
    })();
  }, [
    enabled,
    conversationId,
    userMessage,
    personaResponse,
    warpMarkerEnabled,
    correlationsEnabled,
    lastMood,
    parallax.slot,
    turn.turnId,
    turn.turnEnded,
    turn.failed,
    turn.bites,
    turn.swatEntries,
  ]);
}
